/**
 * Body shape returned by ats-conversation / ff-rest / ats-projects when the api
 * composable is created with `parseError: true`. After an HTTP error, `useApi.ts`
 * runs `JSON.stringify({ ...ctx.error, ...ctx.data })`, so `error.value` is a
 * stringified merge of the fetch error and the response body. Backends in this
 * repo return `{ code, message, statusCode, error }` (NestJS-style).
 *
 * All fields are optional because we may also see runtime fetch errors here.
 */
export interface ParsedApiError {
  code?: string;
  message?: string;
  statusCode?: number;
  error?: string;
}

/**
 * Safely parse the JSON-stringified `error.value` produced by the `parseError: true`
 * api composables (see src/composables/useApi.ts). Returns an empty object on
 * null, non-JSON strings, or non-object payloads — never throws.
 *
 * Prefer the structured fields over `error.value.includes('...')` substring
 * checks when the backend exposes a `code` or a stable `message`.
 */
export function parseApiError(rawError: string | null | undefined): ParsedApiError {
  if (!rawError) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawError);
  } catch {
    return {};
  }

  if (typeof parsed !== 'object' || parsed === null) return {};

  const record = parsed as Record<string, unknown>;
  return {
    code: typeof record.code === 'string' ? record.code : undefined,
    message: typeof record.message === 'string' ? record.message : undefined,
    statusCode: typeof record.statusCode === 'number' ? record.statusCode : undefined,
    error: typeof record.error === 'string' ? record.error : undefined,
  };
}
